/**
 * @module components/ChartSwitch
 * @description 图表类型切换组件（Phase 5 迁移）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('chart_switch')
 *
 * chart_switch 持久化规则：
 *   - 仅打开开关：只写 show=true，chartTypes 为空（UI 隐式勾选当前图表）
 *   - 用户手动勾选后：将全部已选类型（含当前图表）写入 chartTypes
 *   - 外部消费方直接读 config.chart_switch.chartTypes 渲染，不做运行时合并
 */

import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import { Checkbox, Switch } from 'tea-component';
import type { TChartType } from '@dbl/core/chart';
import SvgIcon from '@home/components/SvgIcon';
import ConfigCard from '@/pages/datalab/components/ConfigCard';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import type { IChartSwitchValue } from '../../types/settings/chartSwitch';
import {
  CHART_SWITCH_DISPLAY_ORDER,
  CHART_TYPE_ICON,
  CHART_TYPE_LABEL_IDS,
} from './constants';
import './index.scss';

/** chartTypes 为空时 UI 仅隐式勾选当前图表；有值时以 draft/base 为准 */
function isChartTypeChecked(
  chartType: TChartType,
  chartTypes: TChartType[],
  currentChartType: TChartType | undefined,
): boolean {
  if (chartTypes.length > 0) {
    return chartTypes.includes(chartType);
  }
  return chartType === currentChartType;
}

/** 用户手动勾选/取消后，生成完整 chartTypes（含当前图表，按展示顺序排列） */
function buildCheckedChartTypes(
  chartTypes: TChartType[],
  currentChartType: TChartType | undefined,
  toggleType: TChartType,
  checked: boolean,
): TChartType[] {
  const checkedSet = new Set<TChartType>(
    chartTypes.length > 0
      ? chartTypes
      : currentChartType
        ? [currentChartType]
        : [],
  );
  if (checked) {
    checkedSet.add(toggleType);
  } else {
    checkedSet.delete(toggleType);
  }
  if (currentChartType) {
    checkedSet.add(currentChartType);
  }
  return CHART_SWITCH_DISPLAY_ORDER.filter(t => checkedSet.has(t));
}

/**
 * ChartSwitch — 图表切换配置
 *
 * 展示顺序：表格、折线图、柱状图
 * 当前图表类型：不可取消勾选（disabled）
 */
const ChartSwitch: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value, setValue } = useSettingObjectState('chart_switch');
  const { show, chartTypes } = value;

  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const currentChartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );

  const handleSwitchChange = useCallback(
    (nextShow: boolean) => {
      const next: IChartSwitchValue = { show: nextShow, chartTypes: [] };
      setValue(next);
    },
    [setValue],
  );

  const handleCheckChange = useCallback(
    (type: TChartType, checked: boolean) => {
      if (type === currentChartType) {
        return;
      }
      const nextChartTypes = buildCheckedChartTypes(
        chartTypes,
        currentChartType,
        type,
        checked,
      );
      setValue({ show, chartTypes: nextChartTypes });
    },
    [show, chartTypes, currentChartType, setValue],
  );

  return (
    <ConfigCard
      className="dbl-chart-switch"
      title={<span>{formatMessage({ id: 'dbl_chart_switch' })}</span>}
      operation={<Switch value={show} onChange={handleSwitchChange} />}
    >
      {show && (
        <div className="dbl-chart-switch-content">
          <div className="dbl-chart-switch-list">
            {CHART_SWITCH_DISPLAY_ORDER.map(chartType => {
              const isCurrent = chartType === currentChartType;
              const checked = isChartTypeChecked(
                chartType,
                chartTypes,
                currentChartType,
              );
              return (
                <div key={chartType} className="dbl-chart-switch-list-item">
                  <Checkbox
                    value={checked}
                    disabled={isCurrent}
                    onChange={v => handleCheckChange(chartType, v)}
                  >
                    <SvgIcon type={CHART_TYPE_ICON[chartType] ?? ''} />
                    {formatMessage({
                      id: CHART_TYPE_LABEL_IDS[chartType] ?? chartType,
                    })}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </ConfigCard>
  );
};

export default React.memo(ChartSwitch);

